import {
  AddScheduleEmisionIcon,
  AiRedoIcon,
  CheckThinyIcon,
  CloseIcon,
  DuplicateIcon,
  EditIcon,
  EyeIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonShapeEnumOld,
  IconSizeEnum,
  InactiveIcon,
  ManualEditIcon,
  PauseIsolatedIcon,
  PlayIsolatedIcon,
  PlayLineIcon,
  RefreshIcon,
  ReportCNMCIcon,
  ResetCodeIcon,
  SegmentListIcon,
  SendIcon,
  SendTimeIcon,
  SettingsIcon,
  Tooltip,
  TrashDeleteIcon,
  UnLinkIcon,
  UploadFileIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export type RenderActionButtonsTooltips = {
  play?: string;
  pause?: string;
  preview?: string;
  segments?: string;
  update?: string;
  unlink?: string;
  addScheduleEmisionIcon?: string;
  delete?: string;
};

interface ActionProps<Item> {
  alwaysVisible?: boolean;
  item: Item;
  onUpdate?: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onDuplicate?: (item: Item) => void;
  onSendMailing?: (item: Item) => void;
  onScheduledShipping?: (item: Item) => void;
  onPreview?: (item: Item) => void;
  onCheck?: (item: Item) => void;
  onValidate?: (item: Item) => void;
  onInvalidate?: (item: Item) => void;
  onRegenerate?: (item: Item) => void;
  onRelaunch?: (item: Item) => void;
  onCancel?: (item: Item) => void;
  onManualEdit?: (item: Item) => void;
  onManualPlay?: (item: Item) => void;
  onUnLink?: (item: Item) => void;
  onSettings?: (item: Item) => void;
  onSegments?: (item: Item) => void;
  onAiRedo?: (item: Item) => void;
  onPlay?: (item: Item) => void;
  onPause?: (item: Item) => void;
  onAddScheduleEmisionIcon?: (item: Item) => void;
  onReportDownload?: (item: Item) => void;
  onFilesUpload?: (item: Item) => void;
  disabledFields?: {
    update?: boolean;
    delete?: boolean;
    pause?: boolean;
    relaunch?: boolean;
    error?: boolean;
    duplicate?: boolean;
    sendMailing?: boolean;
    preview?: boolean;
    check?: boolean;
    validate?: boolean;
    regenerate?: boolean;
    manualEdit?: boolean;
    onUnLink?: boolean;
    settings?: boolean;
    scheduledShipping?: boolean;
    ai?: boolean;
    play?: boolean;
    segments?: boolean;
    addScheduleEmisionIcon?: boolean;
    reportDownload?: boolean;
  };
  actionPermissions?: {
    delete?: string;
  };
  tooltips?: RenderActionButtonsTooltips;
}

export const RenderActionButtons = <Item = any,>({
  alwaysVisible = false,
  item,
  onUpdate,
  onDelete,
  onDuplicate,
  onSendMailing,
  onPreview,
  onCheck,
  onValidate,
  onInvalidate,
  onRegenerate,
  onManualEdit,
  onManualPlay,
  onUnLink,
  onSettings,
  onScheduledShipping,
  onAiRedo,
  onPlay,
  onPause,
  onSegments,
  onRelaunch,
  onCancel,
  onAddScheduleEmisionIcon,
  onReportDownload,
  onFilesUpload,
  disabledFields,
  tooltips,
}: ActionProps<Item>) => {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        'flex justify-end transition duration-700',
        alwaysVisible
          ? ''
          : 'group-hover:bg-text-accent-primary-30 opacity-100 group-hover:opacity-100 @3xl/table:opacity-0'
      )}
    >
      {onCheck && (
        <Tooltip help={t('Seleccionar')} grow>
          <IconButton
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onCheck(item)}
            className="select-none"
          >
            <CheckThinyIcon />
          </IconButton>
        </Tooltip>
      )}

      {onValidate && (
        <Tooltip help={t('Marcar como revisado')} grow>
          <IconButton
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onValidate(item)}
            className="select-none"
          >
            <CheckThinyIcon />
          </IconButton>
        </Tooltip>
      )}
      {onInvalidate && (
        <Tooltip help={t('Anular revisión')} grow>
          <IconButton
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onInvalidate(item)}
            className="select-none"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}
      {onUpdate && (
        <Tooltip help={tooltips?.update ?? t('Editar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onUpdate(item)}
            disabled={disabledFields?.update}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      {onPlay && (
        <Tooltip help={tooltips?.play} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onPlay(item)}
            disabled={disabledFields?.play}
          >
            <PlayIsolatedIcon />
          </IconButton>
        </Tooltip>
      )}
      {onPreview && (
        <Tooltip help={tooltips?.preview ?? t('Previsualizar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onPreview(item)}
            disabled={disabledFields?.preview}
          >
            <EyeIcon />
          </IconButton>
        </Tooltip>
      )}
      {onSegments && (
        <Tooltip help={tooltips?.segments ?? 'Ir al corte'} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onSegments(item)}
            disabled={disabledFields?.delete}
          >
            <SegmentListIcon />
          </IconButton>
        </Tooltip>
      )}
      {onPause && (
        <Tooltip help={tooltips?.pause ?? t('Pausar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onPause(item)}
            disabled={disabledFields?.pause}
          >
            <PauseIsolatedIcon />
          </IconButton>
        </Tooltip>
      )}
      {onFilesUpload && (
        <Tooltip help={t('Subir archivos')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onFilesUpload(item)}
          >
            <UploadFileIcon />
          </IconButton>
        </Tooltip>
      )}
      {onReportDownload && (
        <Tooltip help={t('Descargar informe')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onReportDownload(item)}
            disabled={disabledFields?.reportDownload}
          >
            <ReportCNMCIcon />
          </IconButton>
        </Tooltip>
      )}
      {onRelaunch && (
        <Tooltip help={t('Relanzar proceso')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onRelaunch(item)}
            disabled={disabledFields?.relaunch}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      )}
      {onSettings && (
        <Tooltip help={t('Configurar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onSettings(item)}
            disabled={disabledFields?.settings}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      )}
      {onManualEdit && (
        <Tooltip help={t('Editar manualmente')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onManualEdit(item)}
            disabled={disabledFields?.manualEdit}
          >
            <ManualEditIcon />
          </IconButton>
        </Tooltip>
      )}
      {onManualPlay && (
        <Tooltip help={t('Ejecución manual')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onManualPlay(item)}
          >
            <PlayLineIcon />
          </IconButton>
        </Tooltip>
      )}
      {onRegenerate && (
        <Tooltip help={t('Regenerar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onRegenerate(item)}
            disabled={disabledFields?.regenerate}
          >
            <ResetCodeIcon size={IconSizeEnum.MD} />
          </IconButton>
        </Tooltip>
      )}
      {onSendMailing && (
        <Tooltip help={t('Enviar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onSendMailing(item)}
          >
            <SendIcon />
          </IconButton>
        </Tooltip>
      )}
      {onScheduledShipping && (
        <Tooltip help={t('Programar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onScheduledShipping(item)}
          >
            <SendTimeIcon />
          </IconButton>
        </Tooltip>
      )}
      {onCancel && (
        <Tooltip help={t('Cancelar proceso')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onCancel(item)}
            disabled={disabledFields?.error}
          >
            <InactiveIcon />
          </IconButton>
        </Tooltip>
      )}
      {onAiRedo && (
        <Tooltip help={t('Relanzar proceso')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onAiRedo(item)}
            disabled={disabledFields?.delete}
          >
            <AiRedoIcon />
          </IconButton>
        </Tooltip>
      )}
      {onDuplicate && (
        <Tooltip help={t('Duplicar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onDuplicate(item)}
            disabled={disabledFields?.duplicate}
          >
            <DuplicateIcon />
          </IconButton>
        </Tooltip>
      )}
      {onUnLink && (
        <Tooltip help={tooltips?.unlink ?? t('Desvincular')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onUnLink(item)}
            disabled={disabledFields?.onUnLink}
          >
            <UnLinkIcon />
          </IconButton>
        </Tooltip>
      )}
      {onAddScheduleEmisionIcon && (
        <Tooltip help={tooltips?.addScheduleEmisionIcon ?? t('Añadir a parrilla')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onAddScheduleEmisionIcon(item)}
            disabled={disabledFields?.addScheduleEmisionIcon}
          >
            <AddScheduleEmisionIcon />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip help={tooltips?.delete ?? t('Borrar')} grow>
          <IconButton
            className="select-none"
            color={IconButtonColorEnum.NONE_DEFAULT}
            shape={IconButtonShapeEnumOld.OLD_SQUARED}
            onClick={() => onDelete(item)}
            disabled={disabledFields?.delete}
          >
            <TrashDeleteIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};
